import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectors } from 'features/dashboard'
import { Button, Form, FormGroup, Label, Spinner } from 'reactstrap'
import { Field, Formik } from 'formik'
import { customInputForm } from 'ui/common'
import { getTeams } from 'features/dashboard/Team.actionTypes'
import { sendSupportTicket } from 'features/dashboard/SupportEmail.actionTypes'
import { contactUsEmail } from 'features/user/actionTypes'
import UploadImage from './UploadImage'
import { HiOutlineTrash } from 'react-icons/hi'
import { compressImage, getShortFileName } from '../MapComponents/helpers'

const Contact = () => {
    const user = useSelector(selectors.getUser)
    const dispatch = useDispatch()
    const team = useSelector(selectors.getTeam)
    const [files, setFiles] = useState([])

    const sharedStyles = {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 9,
        border: '1px solid #ccc',
    }

    useEffect(() => {
        dispatch(getTeams())
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (data, action) => {
        const formData = new FormData()

        formData.append('subject', data.subject)
        formData.append('priority', data.priority.toLowerCase())
        formData.append('type', data.ticketType.replace(' ', '_').toLowerCase())
        formData.append('team', data.team)
        formData.append('message', data.message)
        formData.append('user_email', user.profile.email)
        formData.append('environment', 'production')

        // Field names expected by the backend
        const attachmentFields = ['attachment_one', 'attachment_two', 'attachment_three']

        const processedFiles = await Promise.all(
            files.map(async (file, index) => {
                const isImage = file.type?.startsWith('image/')
                let blob

                if (isImage) {
                    const base64 = await compressImage(file)
                    const response = await fetch(base64)
                    blob = await response.blob()
                } else {
                    blob =
                        file instanceof File
                            ? file
                            : new Blob([file], { type: file.type || 'application/octet-stream' })
                }

                const fileName =
                    file.name || `file-${index + 1}.${blob.type.split('/')[1] || 'bin'}`
                const realFile = new File([blob], fileName, { type: blob.type })

                return { index, file: realFile }
            })
        )

        processedFiles.forEach(({ index, file }) => {
            if (attachmentFields[index]) {
                formData.append(attachmentFields[index], file)
            }
        })

        dispatch(sendSupportTicket(formData)).then((response) => {
            if (response.error) {
                action.setSubmitting(false)
                action.setStatus('error')
                setFiles([])
                return
            }

            action.setSubmitting(false)
            dispatch(contactUsEmail())
            action.setStatus('sent')
            action.resetForm()
            setFiles([])
        })
    }

    return (
        <div className="d-flex flex-column justify-content-center align-items-center">
            <div className="d-flex flex-column justify-content-center align-items-center my-5">
                <span className="text-meili">Get in touch</span>
                <h1>Contact us</h1>
                <span className="w-50">
                    <strong> Note: </strong> Our documentation will occasionally be updated with new
                    information, and might have the answer you’re looking for. If you cannot find
                    the answer, please feel free to contact us below.
                </span>
            </div>
            <div className="w-50">
                <Formik
                    initialValues={{
                        priority: 'Medium',
                        team: '',
                        organization: team.details.organization?.title,
                        ticketType: '',
                    }}
                    onSubmit={handleSubmit}
                >
                    {({ values, isSubmitting, setFieldValue, handleSubmit }) => (
                        <Form
                            onSubmit={(e) => {
                                e.preventDefault()
                                handleSubmit()
                            }}
                        >
                            <FormGroup row>
                                <Label for="subject" />
                                Subject
                                <Field
                                    id="subject"
                                    component={customInputForm}
                                    type="text"
                                    name="subject"
                                    placeholder="Add subject line"
                                    required
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '10px',
                                        padding: '8px',
                                        fontSize: '16px',
                                        height: '3rem',
                                    }}
                                    value={values.subject || ''}
                                />
                            </FormGroup>
                            <FormGroup row>
                                <Label for="priority" />
                                Priority
                                <Field
                                    id="priority"
                                    component={customInputForm}
                                    type="select"
                                    name="priority"
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '10px',
                                        padding: '8px',
                                        fontSize: '16px',
                                        height: '3rem',
                                    }}
                                    value={values.priority || 'Medium'}
                                >
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </Field>
                            </FormGroup>
                            <FormGroup row>
                                <Label for="organization" />
                                Organisation
                                <Field
                                    id="organization"
                                    component={customInputForm}
                                    type="string"
                                    name="organization"
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '10px',
                                        padding: '8px',
                                        fontSize: '16px',
                                        height: '3rem',
                                    }}
                                    value={values.organization || ''}
                                />
                            </FormGroup>
                            <FormGroup row>
                                <Label for="team" />
                                Team
                                <Field
                                    id="team"
                                    component={customInputForm}
                                    type="select"
                                    name="team"
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '10px',
                                        padding: '8px',
                                        fontSize: '16px',
                                        height: '3rem',
                                    }}
                                    value={values.team || ''}
                                    onChange={(e) => {
                                        const team = e.target.value
                                        if (!team) return
                                        setFieldValue('team', team)
                                    }}
                                >
                                    <option value=""> -- Select team -- </option>
                                    {team.list?.map((team) => (
                                        <option key={team.uuid} value={team.uuid}>
                                            {team.title}
                                        </option>
                                    ))}
                                </Field>
                            </FormGroup>
                            <FormGroup row>
                                <Label for="message" />
                                Message
                                <Field
                                    component={customInputForm}
                                    type="textarea"
                                    name="message"
                                    required
                                    placeholder="Let us know what we can help you with..."
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '10px',
                                        padding: '8px',
                                        fontSize: '16px',
                                        height: '15rem',
                                    }}
                                    value={values.message || ''}
                                />
                            </FormGroup>
                            <FormGroup row>
                                <Label for="ticketType" />
                                Type of ticket
                                <Field
                                    id="ticketType"
                                    component={customInputForm}
                                    type="select"
                                    name="ticketType"
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '10px',
                                        padding: '8px',
                                        fontSize: '16px',
                                        height: '3rem',
                                    }}
                                    value={values.ticketType || ''}
                                >
                                    <option value="">-- Ticket type --</option>
                                    <option value="administration">Administration</option>
                                    <option value="bug_report">Bug reporting</option>
                                    <option value="feature_request">Feature request</option>
                                    <option value="issue">Issue</option>
                                    <option value="other">Other</option>
                                </Field>
                            </FormGroup>
                            {files.length < 5 && (
                                <FormGroup row>
                                    <Label for="attachFile" />
                                    Attach file
                                    <UploadImage setFile={setFiles} file={files} fileType="image" />
                                </FormGroup>
                            )}
                            {files.length > 0 && (
                                <FormGroup row>
                                    <Label>Attached files</Label>
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '10px',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {files.map((file, idx) => {
                                            const isString = typeof file === 'string'
                                            const url = isString ? file : URL.createObjectURL(file)
                                            const fileType = (isString ? file : file.type) || ''

                                            let content

                                            if (fileType.startsWith('image/')) {
                                                content = (
                                                    <img
                                                        src={url}
                                                        alt={`uploaded-${idx}`}
                                                        style={sharedStyles}
                                                    />
                                                )
                                            } else if (fileType.startsWith('video/')) {
                                                content = (
                                                    <video
                                                        style={{
                                                            width: '90px',
                                                            height: '90px',
                                                            objectFit: 'cover',
                                                            borderRadius: '9px',
                                                            border: '1px solid #ccc',
                                                        }}
                                                        src={url}
                                                        muted
                                                        loop
                                                        autoPlay
                                                        playsInline
                                                    />
                                                )
                                            } else {
                                                const name = isString
                                                    ? url.split('/').pop()
                                                    : file.name
                                                content = (
                                                    <div
                                                        style={{
                                                            ...sharedStyles,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: 12,
                                                            backgroundColor: '#f0f0f0',
                                                            padding: 5,
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {getShortFileName(name)}
                                                    </div>
                                                )
                                            }

                                            return (
                                                <div
                                                    key={idx}
                                                    className="image-wrapper"
                                                    style={{
                                                        position: 'relative',
                                                        width: '90px',
                                                        height: '90px',
                                                        margin: 5,
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => {
                                                        const newFiles = [...files]
                                                        newFiles.splice(idx, 1)
                                                        setFiles(newFiles)
                                                    }}
                                                >
                                                    {content}
                                                    <div
                                                        className="trash-icon"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            right: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <HiOutlineTrash size={20} color="black" />
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </FormGroup>
                            )}
                            <FormGroup row>
                                <Button
                                    disabled={isSubmitting}
                                    type="submit"
                                    className="save-btn-modal modals-new-btns"
                                    style={{
                                        width: '100%',
                                        borderRadius: '0.5rem',
                                        marginTop: '1rem',
                                    }}
                                >
                                    Send message
                                    {isSubmitting && (
                                        <Spinner size="sm" className="ml-2 text-white" />
                                    )}
                                </Button>
                            </FormGroup>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    )
}

export default Contact
